import { useEffect, useState } from 'react'
import { useWatch, type UseFormReturn } from 'react-hook-form'

import { friendlyErrorKey } from '@/lib/api/friendly-errors'

import { activityComposerApi } from '../api'
import type { StoredLocation } from '../draft'
import type {
  ActivityDetail,
  CitySuggestion,
  PlaceSuggestion,
} from '../types'
import type { ActivityFormValues } from '../values'
import { useDebouncedValue } from './use-debounced-value'

const MIN_QUERY_LENGTH = 2
const CITY_DEBOUNCE_MS = 700
const PLACE_DEBOUNCE_MS = 800

export type SearchPhase = 'idle' | 'loading' | 'ready' | 'error'

interface SearchResult<Item> {
  query: string
  phase: SearchPhase
  items: Item[]
  errorKey: string | null
}

function emptyResult<Item>(): SearchResult<Item> {
  return { query: '', phase: 'idle', items: [], errorKey: null }
}

function hasCoordinates(
  value: CitySuggestion | null,
): value is CitySuggestion {
  return (
    value !== null &&
    Number.isFinite(value.latitude) &&
    Number.isFinite(value.longitude)
  )
}

export interface LocationPayload {
  city?: string
  locationName?: string
  latitude?: number
  longitude?: number
}

export interface LocationValidationError {
  field: 'city' | 'locationName'
  message: string
}

export interface LocationSelection {
  cityValue: string
  placeValue: string
  selectedCity: CitySuggestion | null
  selectedPlace: PlaceSuggestion | null
  manualCity: boolean
  manualPlace: boolean
  canSearchPlaces: boolean
  cityPhase: SearchPhase
  cityItems: CitySuggestion[]
  cityErrorKey: string | null
  cityAttempted: boolean
  placePhase: SearchPhase
  placeItems: PlaceSuggestion[]
  placeErrorKey: string | null
  placeAttempted: boolean
  isUnchanged: boolean
  hasSelectedCity: boolean
  hasSelectedPlace: boolean
  setCity: (value: string) => void
  setPlace: (value: string) => void
  chooseCity: (suggestion: CitySuggestion) => void
  choosePlace: (suggestion: PlaceSuggestion) => void
  useManualCity: () => void
  useManualPlace: () => void
  searchCityAgain: () => void
  searchPlaceAgain: () => void
  retryCity: () => void
  retryPlace: () => void
  validate: () => LocationValidationError | null
  toPayload: () => LocationPayload
}

interface UseLocationSelectionArgs {
  form: UseFormReturn<ActivityFormValues>
  mode: 'create' | 'edit'
  activity?: ActivityDetail
  initialLocation?: StoredLocation | null
}

function initialCitySuggestion(
  activity: ActivityDetail | undefined,
): CitySuggestion | null {
  const city = activity?.city?.trim()
  if (!city) return null
  return {
    city,
    label: city,
    latitude: activity?.latitude ?? Number.NaN,
    longitude: activity?.longitude ?? Number.NaN,
  }
}

function initialPlaceSuggestion(
  activity: ActivityDetail | undefined,
): PlaceSuggestion | null {
  const label = activity?.locationName?.trim()
  if (!label) return null
  return {
    label,
    latitude: activity?.latitude ?? Number.NaN,
    longitude: activity?.longitude ?? Number.NaN,
  }
}

/**
 * City/place search and selection for the activity form.
 *
 * - Suggestions come only from the backend (`/location/suggestions` and
 *   `/location/places`), never from a third-party geocoder in the browser.
 * - Stale requests are aborted and queries are debounced.
 * - Choosing a different city clears the place and the meeting label.
 * - Editing keeps the existing location when it is unchanged (`f472f9a`).
 * - When suggestions fail, return nothing or have no usable coordinates, the
 *   user can explicitly switch to manual text entry (no coordinates are sent).
 */
export function useLocationSelection({
  form,
  mode,
  activity,
  initialLocation,
}: UseLocationSelectionArgs): LocationSelection {
  const [selectedCity, setSelectedCity] = useState<CitySuggestion | null>(
    initialLocation?.selectedCity ?? initialCitySuggestion(activity),
  )
  const [selectedPlace, setSelectedPlace] = useState<PlaceSuggestion | null>(
    initialLocation?.selectedPlace ?? initialPlaceSuggestion(activity),
  )
  const [manualCity, setManualCity] = useState(
    initialLocation?.manualCity ?? false,
  )
  const [manualPlace, setManualPlace] = useState(
    initialLocation?.manualPlace ?? false,
  )
  const [cityResult, setCityResult] =
    useState<SearchResult<CitySuggestion>>(emptyResult)
  const [placeResult, setPlaceResult] =
    useState<SearchResult<PlaceSuggestion>>(emptyResult)
  const [cityAttempt, setCityAttempt] = useState(0)
  const [placeAttempt, setPlaceAttempt] = useState(0)

  const cityValue = useWatch({ control: form.control, name: 'city' }) ?? ''
  const placeValue =
    useWatch({ control: form.control, name: 'locationName' }) ?? ''

  const cityTrimmed = cityValue.trim()
  const placeTrimmed = placeValue.trim()
  const debouncedCity = useDebouncedValue(cityTrimmed, CITY_DEBOUNCE_MS)
  const debouncedPlace = useDebouncedValue(placeTrimmed, PLACE_DEBOUNCE_MS)

  const canSearchPlaces = hasCoordinates(selectedCity)

  useEffect(() => {
    if (
      selectedCity ||
      manualCity ||
      debouncedCity.length < MIN_QUERY_LENGTH
    ) {
      return
    }
    const controller = new AbortController()
    activityComposerApi
      .suggestCities(debouncedCity, { signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return
        setCityResult({
          query: debouncedCity,
          phase: 'ready',
          items: response.items,
          errorKey: null,
        })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setCityResult({
          query: debouncedCity,
          phase: 'error',
          items: [],
          errorKey: friendlyErrorKey(error),
        })
      })
    return () => controller.abort()
  }, [cityAttempt, debouncedCity, manualCity, selectedCity])

  useEffect(() => {
    if (
      !canSearchPlaces ||
      !selectedCity ||
      selectedPlace ||
      manualPlace ||
      debouncedPlace.length < MIN_QUERY_LENGTH
    ) {
      return
    }
    const controller = new AbortController()
    activityComposerApi
      .suggestPlaces(
        debouncedPlace,
        selectedCity.city,
        selectedCity.latitude,
        selectedCity.longitude,
        { signal: controller.signal },
      )
      .then((response) => {
        if (controller.signal.aborted) return
        setPlaceResult({
          query: debouncedPlace,
          phase: 'ready',
          items: response.items,
          errorKey: null,
        })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        setPlaceResult({
          query: debouncedPlace,
          phase: 'error',
          items: [],
          errorKey: friendlyErrorKey(error),
        })
      })
    return () => controller.abort()
  }, [
    canSearchPlaces,
    debouncedPlace,
    manualPlace,
    placeAttempt,
    selectedCity,
    selectedPlace,
  ])

  const cityStable =
    cityResult.query === debouncedCity && debouncedCity === cityTrimmed
  const cityPhase: SearchPhase =
    manualCity ||
    cityTrimmed.length < MIN_QUERY_LENGTH ||
    (selectedCity !== null &&
      cityTrimmed === selectedCity.label.trim())
      ? 'idle'
      : cityStable
        ? cityResult.phase
        : 'loading'

  const placeStable =
    placeResult.query === debouncedPlace && debouncedPlace === placeTrimmed
  const placePhase: SearchPhase =
    !canSearchPlaces ||
    manualPlace ||
    placeTrimmed.length < MIN_QUERY_LENGTH ||
    (selectedPlace !== null &&
      placeTrimmed === selectedPlace.label.trim())
      ? 'idle'
      : placeStable
        ? placeResult.phase
        : 'loading'

  const isUnchanged =
    mode === 'edit' &&
    activity !== undefined &&
    (activity.city ?? '').trim() === cityTrimmed &&
    (activity.locationName ?? '').trim() === placeTrimmed

  const hasSelectedCity =
    selectedCity !== null && cityTrimmed === selectedCity.label.trim()
  const hasSelectedPlace =
    selectedPlace !== null && placeTrimmed === selectedPlace.label.trim()

  function clearPlaceSelection(): void {
    if (selectedPlace) setSelectedPlace(null)
    if (manualPlace) setManualPlace(false)
    form.clearErrors('locationName')
    if (form.getValues('locationName') !== '') {
      form.setValue('locationName', '', {
        shouldDirty: true,
        shouldTouch: true,
      })
    }
    setPlaceResult(emptyResult())
  }

  function setCity(value: string): void {
    form.setValue('city', value, { shouldDirty: true, shouldTouch: true })
    if (value.trim() !== '') form.clearErrors('city')
    if (selectedCity && value.trim() === selectedCity.label.trim()) return
    if (selectedCity) setSelectedCity(null)
    clearPlaceSelection()
    setCityResult(emptyResult())
  }

  function setPlace(value: string): void {
    form.setValue('locationName', value, {
      shouldDirty: true,
      shouldTouch: true,
    })
    form.clearErrors('locationName')
    if (selectedPlace && value.trim() === selectedPlace.label.trim()) return
    if (selectedPlace) setSelectedPlace(null)
    if (!canSearchPlaces && value.trim() !== '') {
      setManualPlace(true)
    }
    setPlaceResult(emptyResult())
  }

  function chooseCity(suggestion: CitySuggestion): void {
    setSelectedCity(suggestion)
    setManualCity(false)
    setSelectedPlace(null)
    setManualPlace(false)
    setCityResult(emptyResult())
    setPlaceResult(emptyResult())
    form.clearErrors('city')
    form.setValue('city', suggestion.label, {
      shouldDirty: true,
      shouldTouch: true,
    })
    form.setValue('locationName', '', {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  function choosePlace(suggestion: PlaceSuggestion): void {
    setSelectedPlace(suggestion)
    setManualPlace(false)
    setPlaceResult(emptyResult())
    form.clearErrors('locationName')
    form.setValue('locationName', suggestion.label, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  function useManualCity(): void {
    setManualCity(true)
    setSelectedCity(null)
    setSelectedPlace(null)
    setManualPlace(false)
    setCityResult(emptyResult())
    setPlaceResult(emptyResult())
    form.clearErrors('city')
    if (form.getValues('locationName') !== '') {
      form.setValue('locationName', '', {
        shouldDirty: true,
        shouldTouch: true,
      })
    }
  }

  function useManualPlace(): void {
    setManualPlace(true)
    setSelectedPlace(null)
    setPlaceResult(emptyResult())
    form.clearErrors('locationName')
  }

  function searchCityAgain(): void {
    setManualCity(false)
    setCityResult(emptyResult())
  }

  function searchPlaceAgain(): void {
    setManualPlace(false)
    setPlaceResult(emptyResult())
  }

  function retryCity(): void {
    setCityResult(emptyResult())
    setCityAttempt((value) => value + 1)
  }

  function retryPlace(): void {
    setPlaceResult(emptyResult())
    setPlaceAttempt((value) => value + 1)
  }

  function validate(): LocationValidationError | null {
    const cityName = selectedCity?.city ?? cityTrimmed
    if (!isUnchanged && cityName.trim() === '') {
      return {
        field: 'city',
        message: 'activityForm.validation.cityRequired',
      }
    }
    if (!isUnchanged && !hasSelectedCity && !manualCity) {
      return {
        field: 'city',
        message: 'activityForm.validation.citySelection',
      }
    }
    if (placeTrimmed === '') return null
    if (isUnchanged || hasSelectedPlace || manualPlace) return null
    return {
      field: 'locationName',
      message: 'activityForm.validation.placeSelection',
    }
  }

  function toPayload(): LocationPayload {
    const cityName = selectedCity?.city ?? cityTrimmed
    const locationName = placeTrimmed
    const selectedCoordinates =
      hasSelectedPlace && selectedPlace
        ? {
            latitude: selectedPlace.latitude,
            longitude: selectedPlace.longitude,
          }
        : null
    const reusedCoordinates =
      isUnchanged &&
      locationName !== '' &&
      activity?.latitude !== null &&
      activity?.latitude !== undefined &&
      activity?.longitude !== null &&
      activity?.longitude !== undefined
        ? {
            latitude: activity.latitude,
            longitude: activity.longitude,
          }
        : null
    const coordinates = selectedCoordinates ?? reusedCoordinates

    return {
      city: cityName || undefined,
      locationName:
        mode === 'edit' || locationName !== '' ? locationName : undefined,
      latitude: coordinates?.latitude,
      longitude: coordinates?.longitude,
    }
  }

  return {
    cityValue,
    placeValue,
    selectedCity,
    selectedPlace,
    manualCity,
    manualPlace,
    canSearchPlaces,
    cityPhase,
    cityItems: cityStable ? cityResult.items : [],
    cityErrorKey:
      cityPhase === 'error' && cityStable ? cityResult.errorKey : null,
    cityAttempted: cityPhase === 'ready' && cityStable,
    placePhase,
    placeItems: placeStable ? placeResult.items : [],
    placeErrorKey:
      placePhase === 'error' && placeStable ? placeResult.errorKey : null,
    placeAttempted: placePhase === 'ready' && placeStable,
    isUnchanged,
    hasSelectedCity,
    hasSelectedPlace,
    setCity,
    setPlace,
    chooseCity,
    choosePlace,
    useManualCity,
    useManualPlace,
    searchCityAgain,
    searchPlaceAgain,
    retryCity,
    retryPlace,
    validate,
    toPayload,
  }
}
